import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import CarCard from "../components/cars/CarCard";
import FilterBar from "../components/FilterBar";
import { useCurrency } from "../context/CurrencyContext";
import { Search, SlidersHorizontal, MapPin, X } from "lucide-react";

const CAR_TYPES = ["SUV", "Sedan", "Hatchback", "Van", "Pickup"];
const TRANSMISSIONS = ["Automatic", "Manual"];
const FUELS = ["Petrol", "Diesel", "Hybrid", "Electric", "Plugin-Hybrid"];

export default function Marketplace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { countries } = useCurrency();

  const [filters, setFilters] = useState({
    city: searchParams.get("city") || "",
    country_code: searchParams.get("country") || "",
    car_type: searchParams.get("car_type") || "",
    category: searchParams.get("category") || "",
    transmission: "",
    fuel: "",
    seats: "",
    provides_driver: searchParams.get("provides_driver") || "",  // "" | "true" | "false"
    driver_mandatory: searchParams.get("driver_mandatory") || "",  // "" | "true"
    sponsored: searchParams.get("sponsored") || "",  // "" | "true"
    price: [0, 300000],
    q: searchParams.get("q") || "",
    from: searchParams.get("from") || "",
    until: searchParams.get("until") || "",
  });
  const [cars, setCars] = useState([]);
  const sortCars = (list, key) => {
    const s = [...list];
    if (key === "price_asc") s.sort((a, b) => a.price_per_day - b.price_per_day);
    else if (key === "price_desc") s.sort((a, b) => b.price_per_day - a.price_per_day);
    else if (key === "rating") s.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return s;
  };
  const sortByRef = useRef("default");
  const [loading, setLoading] = useState(true);

  const fetchCars = async (f = filters) => {
    setLoading(true);
    try {
      const params = {
        ...(f.city && { city: f.city }),
        ...(f.country_code && { country_code: f.country_code }),
        ...(f.car_type && { car_type: f.car_type }),
        ...(f.category && { category: f.category }),
        ...(f.transmission && { transmission: f.transmission }),
        ...(f.fuel && { fuel: f.fuel }),
        ...(f.seats && { seats: Number(f.seats) }),
        ...(f.provides_driver !== "" && { provides_driver: f.provides_driver === "true" }),
        ...(f.driver_mandatory === "true" && { driver_mandatory: true }),
        ...(f.sponsored === "true" && { sponsored: true }),
        ...(f.q && { q: f.q }),
        ...(f.from && { start_date: f.from }),
        ...(f.until && { end_date: f.until }),
        ...(f.price[0] > 0 && { min_price: f.price[0] }),
        ...(f.price[1] < 300000 && { max_price: f.price[1] }),
      };
      const { data } = await api.get("/cars", { params });
      setCars(sortCars(data, sortByRef.current));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const newFilters = {
      city: searchParams.get("city") || "",
      country_code: searchParams.get("country") || "",
      car_type: searchParams.get("car_type") || "",
      category: searchParams.get("category") || "",
      transmission: "",
      fuel: "",
      seats: "",
      provides_driver: searchParams.get("provides_driver") || "",
      sponsored: searchParams.get("sponsored") || "",
      price: [0, 300000],
      q: searchParams.get("q") || "",
      from: searchParams.get("from") || "",
      until: searchParams.get("until") || "",
    };
    setFilters(newFilters);
    fetchCars(newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onApply = (e) => {
    e?.preventDefault?.();
    setShowFilters(false);
    fetchCars();
  };

  const onClear = () => {
    const f = { city: "", country_code: "", car_type: "", category: "", transmission: "", fuel: "", seats: "", provides_driver: "", driver_mandatory: "", sponsored: "", price: [0, 300000], q: "" };
    setFilters(f);
    fetchCars(f);
  };

  const [openDropdown, setOpenDropdown] = React.useState(null);
  const [sortBy, setSortBy] = React.useState("default");
  const [showFiltersSheet, setShowFiltersSheet] = React.useState(false);
  const hasActiveFilters = !!(filters.transmission || filters.fuel || filters.q || filters.city || filters.country_code || filters.seats || sortBy !== "default" || filters.price[0] > 0 || filters.price[1] < 300000);

  const isDriverActive = filters.provides_driver === "true" || filters.provides_driver === "false";
  const isCategoryActive = filters.sponsored === "true" || !!filters.category;
  const isTypeActive = !!filters.car_type;
  const noFiltersActive = !isDriverActive && !isCategoryActive && !isTypeActive;

  // Each pill toggles only its own filter — they compound with one another
  // instead of resetting each other (e.g. SUV + With driver can both be active).
  const applyDriver = (val) => { const f = { ...filters, provides_driver: filters.provides_driver === val ? "" : val }; setFilters(f); fetchCars(f); setOpenDropdown(null); };
  const applyType = (val) => { const f = { ...filters, car_type: filters.car_type === val ? "" : val }; setFilters(f); fetchCars(f); setOpenDropdown(null); };
  const applyCategory = (key) => {
    const f = { ...filters };
    if (key === "sponsored") { f.sponsored = filters.sponsored === "true" ? "" : "true"; }
    else { f.category = filters.category === key ? "" : key; }
    setFilters(f); fetchCars(f); setOpenDropdown(null);
  };
  const applyAll = () => { const f = { city: filters.city, q: filters.q, country_code: filters.country_code, transmission: "", fuel: "", seats: "", price: [0, 300000], provides_driver: "", sponsored: "", car_type: "", category: "" }; setFilters(f); fetchCars(f); setOpenDropdown(null); };
  const pill = (active) => "shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap " + (active ? "bg-[#2A9D8F] text-white" : "bg-muted text-muted-foreground hover:bg-muted/80");

  return (
    <PublicLayout>
      {openDropdown && <div className="fixed inset-0 z-40" onClick={() => setOpenDropdown(null)} />}
      {/* Grouped pill bar */}
      <div className="bg-background border-b border-border px-3 sm:px-6 lg:px-8 py-2">
        <div className="mx-auto max-w-7xl flex items-center gap-2">
          <button type="button" onClick={applyAll} className={pill(noFiltersActive)}>All</button>
          <div className="relative shrink-0">
            <button type="button" onClick={() => setOpenDropdown(openDropdown === "driver" ? null : "driver")} className={pill(isDriverActive)}>Driver ▾</button>
            {openDropdown === "driver" && (
              <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-xl shadow-lg z-50 min-w-[130px] overflow-hidden">
                {[["true","With driver"],["false","No driver"]].map(([val,label]) => (
                  <button key={val} type="button" onClick={() => applyDriver(val)} className={"w-full text-left px-4 py-2.5 text-xs font-medium hover:bg-muted transition-colors " + (filters.provides_driver === val ? "text-[#2A9D8F] font-semibold" : "")}>{label}</button>
                ))}
              </div>
            )}
          </div>
          <div className="relative shrink-0">
            <button type="button" onClick={() => setOpenDropdown(openDropdown === "type" ? null : "type")} className={pill(isTypeActive)}>Type ▾</button>
            {openDropdown === "type" && (
              <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-xl shadow-lg z-50 min-w-[130px] overflow-hidden">
                {["SUV","Sedan","Hatchback","Van","Pickup"].map((t) => (
                  <button key={t} type="button" onClick={() => applyType(t)} className={"w-full text-left px-4 py-2.5 text-xs font-medium hover:bg-muted transition-colors " + (filters.car_type === t ? "text-[#2A9D8F] font-semibold" : "")}>{t}</button>
                ))}
              </div>
            )}
          </div>
          <div className="relative shrink-0">
            <button type="button" onClick={() => setOpenDropdown(openDropdown === "category" ? null : "category")} className={pill(isCategoryActive)}>Category ▾</button>
            {openDropdown === "category" && (
              <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-xl shadow-lg z-50 min-w-[140px] overflow-hidden">
                {[["Economy","Economy"],["Standard","Standard"],["Luxury","Luxury"],["sponsored","Sponsored ★"]].map(([key,label]) => (
                  <button key={key} type="button" onClick={() => applyCategory(key)} className={"w-full text-left px-4 py-2.5 text-xs font-medium hover:bg-muted transition-colors " + ((filters.category === key || (key === "sponsored" && filters.sponsored === "true")) ? "text-[#2A9D8F] font-semibold" : "")}>{label}</button>
                ))}
              </div>
            )}
          </div>
          <button type="button" onClick={() => setShowFiltersSheet(true)}
            className={"shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ml-auto " + (hasActiveFilters ? "border-[#2A9D8F] text-[#2A9D8F] bg-[#2A9D8F]/10" : "border-border text-muted-foreground bg-muted")}>
            <SlidersHorizontal size={12} />{hasActiveFilters ? " Filters •" : " Filters"}
          </button>
        </div>
      </div>

      {/* Unified filters bottom sheet */}
      {showFiltersSheet && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={() => setShowFiltersSheet(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-background rounded-t-2xl w-full max-h-[88vh] flex flex-col shadow-xl z-10" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
              <span className="font-display font-semibold text-sm">Filters</span>
              <button type="button" onClick={() => setShowFiltersSheet(false)}><X size={16} /></button>
            </div>
            <div className="overflow-y-auto px-4 py-3 space-y-4 flex-1">
              <input type="text" placeholder="Brand / Model (e.g. Toyota, RAV4…)" value={filters.q || ""}
                onChange={e => setFilters({...filters, q: e.target.value})}
                className="w-full h-8 px-3 text-xs border border-border rounded-lg bg-background outline-none" />
              <input type="text" placeholder="City (e.g. Kigali)" value={filters.city || ""}
                onChange={e => setFilters({...filters, city: e.target.value})}
                className="w-full h-8 px-3 text-xs border border-border rounded-lg bg-background outline-none" />
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Country</div>
                <div className="flex flex-wrap gap-1.5">
                  {[["", "Any"], ...countries.map(c => [c.code, c.name])].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => setFilters({...filters, country_code: val})}
                      className={"px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors " + (filters.country_code === val ? "bg-[#2A9D8F] text-white border-[#2A9D8F]" : "border-border text-muted-foreground hover:bg-muted")}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Transmission</div>
                <div className="flex gap-1.5">
                  {[["", "Any"], ["Automatic", "Auto"], ["Manual", "Manual"]].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => setFilters({...filters, transmission: val})}
                      className={"flex-1 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors " + (filters.transmission === val ? "bg-[#2A9D8F] text-white border-[#2A9D8F]" : "border-border text-muted-foreground hover:bg-muted")}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Fuel</div>
                <div className="flex flex-wrap gap-1.5">
                  {[["", "Any"], ["Gasoline", "Petrol"], ["Diesel", "Diesel"], ["Hybrid", "Hybrid"], ["Electric", "EV"], ["Plugin-Hybrid", "PHEV"]].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => setFilters({...filters, fuel: val})}
                      className={"px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors " + (filters.fuel === val ? "bg-[#2A9D8F] text-white border-[#2A9D8F]" : "border-border text-muted-foreground hover:bg-muted")}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Min seats</div>
                <div className="flex gap-1.5">
                  {[["", "Any"], ["2", "2+"], ["4", "4+"], ["5", "5+"], ["7", "7+"]].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => setFilters({...filters, seats: val})}
                      className={"flex-1 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors " + (filters.seats === val ? "bg-[#2A9D8F] text-white border-[#2A9D8F]" : "border-border text-muted-foreground hover:bg-muted")}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Price/day (RWF)</div>
                  <span className="text-[10px] text-muted-foreground">{(filters.price[0]).toLocaleString()} – {(filters.price[1]).toLocaleString()}</span>
                </div>
                <Slider min={0} max={300000} step={1000} value={filters.price} onValueChange={v => setFilters({...filters, price: v})} className="mt-1" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Sort by</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {[["default","Default"],["price_asc","Price Increase"],["price_desc","Price Decrease"],["rating","Top Rated"]].map(([val,label]) => (
                    <button key={val} type="button"
                      onClick={() => { setSortBy(val); sortByRef.current = val; setCars(prev => sortCars(prev, val)); }}
                      className={"py-1.5 rounded-lg text-[11px] font-semibold border transition-colors text-center " + (sortBy === val ? "bg-[#2A9D8F] text-white border-[#2A9D8F]" : "border-border text-muted-foreground hover:bg-muted")}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-border shrink-0 flex gap-2">
              <button type="button" onClick={() => { fetchCars(filters); setShowFiltersSheet(false); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[#2A9D8F] text-white">Apply</button>
              <button type="button"
                onClick={() => { setSortBy("default"); sortByRef.current = "default"; const f = {city:"",q:"",country_code:"",transmission:"",fuel:"",seats:"",price:[0,300000],provides_driver:"",sponsored:"",car_type:""}; setFilters(f); fetchCars(f); setShowFiltersSheet(false); }}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold border border-border text-muted-foreground">Clear</button>
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
            {filters.city ? `Cars in ${filters.city}` : "Browse all cars"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{loading ? "Loading…" : `${cars.length} car${cars.length === 1 ? "" : "s"} available`}</p>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-[260px] sm:h-[380px] bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        ) : cars.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-lg">
            <div className="font-display text-xl text-foreground mb-2">No cars match those filters</div>
            <p className="text-sm text-muted-foreground">Try widening your search or clearing filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6" data-testid="cars-grid">
            {cars.map((c, i) => <CarCard key={c.id} car={c} idx={i} />)}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
